// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * SmartRecommendations — AI Personalization Panel
 * Shows personalised shopping recs + "also bought" based on scan history.
 * Used on the Home tab dashboard.
 */
const SmartRecommendations = ({ userId, onCompare }) => {
  const [data, setData] = useState(null);
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tipLoading, setTipLoading] = useState(false);

  useEffect(() => {
    if (userId) fetchData();
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/personalization/dashboard/${userId}`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch (e) {
      console.error('SmartRecommendations fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTip = async () => {
    setTipLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/personalization/tip/${userId}`,
        { withCredentials: true }
      );
      setTip(res.data.tip);
    } catch (e) {
      console.error('Tip fetch error:', e);
    } finally {
      setTipLoading(false);
    }
  };

  const handleCompare = (barcode, productName) => {
    if (onCompare) onCompare(barcode, productName);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-4 bg-gray-200 rounded w-2/3" />
        <div className="h-16 bg-gray-100 rounded-xl" />
        <div className="h-16 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  if (!data || (!data.recommendations?.length && !data.also_bought?.length)) {
    return (
      <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 text-sm">Build Your Feed</h3>
            <p className="text-[11px] text-gray-400">Scan products to unlock personalised deals</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Savings estimate — front and center */}
      {data.savings_estimate > 0 && (
        <div className="bg-green-50 border border-green-100 rounded-xl p-3 flex items-center justify-between" data-testid="savings-estimate">
          <div>
            <div className="text-xs font-semibold text-gray-700">You could save today</div>
            <div className="text-[11px] text-gray-400">By choosing cheapest options</div>
          </div>
          <div className="text-lg font-bold text-green-600">£{data.savings_estimate.toFixed(2)}</div>
        </div>
      )}

      {/* AI Savings Tip */}
      {!tip ? (
        <button
          onClick={fetchTip}
          disabled={tipLoading}
          className="w-full flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3 text-left hover:bg-gray-100 transition-all active:scale-[0.98]"
          data-testid="ai-tip-btn"
        >
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-700">{tipLoading ? 'Generating...' : 'Get Your AI Savings Tip'}</div>
            <div className="text-[11px] text-gray-400">Tap to reveal personalised advice</div>
          </div>
        </button>
      ) : (
        <div className="bg-green-50 border border-green-100 rounded-xl p-3.5" data-testid="ai-tip-result">
          <div className="flex items-start gap-2.5">
            <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 leading-relaxed">{tip}</p>
              <button onClick={fetchTip} className="text-[11px] text-green-600 mt-1.5 font-medium hover:text-green-700">
                Refresh tip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shopping Insight */}
      {(data.preferred_retailer || data.shopping_insight) && (
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-center gap-3" data-testid="shopping-insight">
          <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-700">
              {data.shopping_insight || `You usually shop at ${data.preferred_retailer}`}
            </div>
            <div className="text-[11px] text-gray-400">
              {data.total_scans} scans · {data.top_categories?.slice(0, 2).join(' · ') || 'growing your profile'}
            </div>
          </div>
        </div>
      )}

      {/* You Might Also Like */}
      {data.recommendations?.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">You Might Also Like</p>
          <div className="grid grid-cols-2 gap-2">
            {data.recommendations.slice(0, 4).map((rec, i) => (
              <button
                key={i}
                onClick={() => handleCompare(rec.barcode, rec.product_name)}
                className="bg-white rounded-xl p-3 text-left border border-gray-100 hover:border-green-300 hover:shadow-sm transition-all group active:scale-[0.97]"
                data-testid={`rec-product-${i}`}
              >
                <div className="text-xs font-semibold text-gray-800 mb-1 leading-tight line-clamp-2">
                  {rec.product_name}
                </div>
                {rec.cheapest_price && (
                  <div className="text-xs font-bold text-green-600 mb-0.5">from £{rec.cheapest_price.toFixed(2)}</div>
                )}
                {rec.cheapest_retailer && (
                  <div className="text-[10px] text-gray-400 mb-2">at {rec.cheapest_retailer}</div>
                )}
                <div className="bg-green-600 text-white text-[10px] font-semibold py-1 px-2.5 rounded-md text-center group-hover:bg-green-700 transition-colors" data-testid={`rec-compare-btn-${i}`}>
                  Compare
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Customers Also Scan */}
      {data.also_bought?.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Others Also Scan</p>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {data.also_bought.slice(0, 5).map((item, i) => (
              <button
                key={i}
                onClick={() => handleCompare(item.barcode, item.product_name)}
                className="flex-shrink-0 bg-white rounded-xl p-3 text-left border border-gray-100 hover:border-green-300 hover:shadow-sm transition-all min-w-[130px] active:scale-[0.97]"
                data-testid={`also-scan-${i}`}
              >
                <div className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2 mb-1">
                  {item.product_name}
                </div>
                {item.cheapest_price && (
                  <div className="text-xs font-bold text-green-600 mb-0.5">from £{item.cheapest_price.toFixed(2)}</div>
                )}
                {item.cheapest_retailer && (
                  <div className="text-[10px] text-gray-400 mb-2">at {item.cheapest_retailer}</div>
                )}
                <div className="bg-green-600 text-white text-[10px] font-semibold py-1 px-2 rounded-md text-center">
                  View Prices
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartRecommendations;
